import React, { useEffect, useMemo } from 'react';
import Plot from 'react-plotly.js';

import { useSelector } from 'react-redux';
import PowerRegression from 'ml-regression-power';

import { chartColors } from '../../helpers/chartColors';
import { selectUser } from '../../reducers/userSlice';
import { selectRTFO } from '../../reducers/rtfoSlice';
import { useTheme } from '@mui/styles';
import { rtfoConversionFactors } from './rtfoConversionFactors';

export const RTFOFrictionModelChart = ({ frictionModelParams, selectedStages, PpipeArray, PpipeCoefficients, handlePpipeFrictionFitUpdate, sdtType="flush" }) => {

  const theme = useTheme();
  const user = useSelector(selectUser);
  const darkMode = user?.value?.darkMode;
  
  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;
  const settings = currentRTFO?.settings;

  const currentStage = currentRTFO?.stageData?.[currentRTFO?.currentStageName];

  const stepdownData = currentStage?.stepdownData;
  const preSandStepdownData = currentStage?.preSandStepdownData;
  const stepdownParams = currentStage?.stepdownParams;
  const units = currentRTFO?.settings?.units || 'metric';

  const data = useMemo(() => {
    if (!stepdownData || !stepdownData?.length > 0 || !stepdownParams) return [];
    
    return getChartData();

    function getChartData() {
      try {

        const traces = [];

        // add steps from all stages prior to the current one
        for (let stageName of selectedStages) {
          const stageData = currentRTFO?.stageData?.[stageName];
          const stageStepdownData = stageData?.stepdownData;
          const stageStepdownParams = stageData?.stepdownParams;
          if (!stageStepdownData || !stageStepdownData?.length > 0 || !stageStepdownParams || !stageStepdownParams?.isip) continue;
          
          const X = [];
          const Y = [];

          for (let i in stageStepdownData) {
            const step = stageStepdownData[i];
            const fracGradient = ((step?.pressure - stageStepdownParams?.isip) * 1000 / stageStepdownParams?.averageDepth); // in kpa
            X.push((step?.rate * (units === 'field' ? rtfoConversionFactors.rate : 1)) || 1);
            Y.push(fracGradient * (units === 'field' ? rtfoConversionFactors.fracGradient / 1000 : 1) || 1);
          };

          traces.push({
            x: X,
            y: Y,
            mode: 'marker',
            type: 'scatter',
            xaxis: 'x1',
            yaxis: 'y1',
            name: stageName,
            marker: {
              size: 6,
              color: 'lightgrey',
              opacity: 0.8
            },
            line: {
              width: 0
            },
            showlegend: false,
            hoverinfo: 'none'
          });
        };

        // add steps from the current stage
        const XCurrent = [];
        const YCurrent = [];
        const XCurrentPreSand = [];
        const YCurrentPreSand = [];

        for (let i in stepdownData) {
          if (!stepdownParams?.isip) continue;
          const step = stepdownData[i];
          const fracGradient = ((step?.pressure - stepdownParams?.isip) * 1000 / stepdownParams?.averageDepth);
          XCurrent.push(step.rate * (units === 'field' ? rtfoConversionFactors.rate : 1) || 1);
          YCurrent.push(fracGradient * (units === 'field' ? rtfoConversionFactors.fracGradient / 1000 : 1) || 1);
        };
        traces.push({
          x: XCurrent,
          y: YCurrent,
          mode: 'marker',
          type: 'scatter',
          xaxis: 'x1',
          yaxis: 'y1',
          name: `${currentRTFO?.currentStageName?.split('_')?.slice(1, )?.join(' ')} - Flush`,
          marker: {
            size: 8,
            color: chartColors[0],
            opacity: 0.9
          },
          line: {
            width: 0
          },
          hoverinfo: 'text',
          hovertext: YCurrent.map(y => `Flush FG: ${y?.toFixed(2)} ${units === 'field' ? 'psi/ft' : 'kPa/m'}`),
        });

        if (preSandStepdownData?.length > 0) {
          for (let i in preSandStepdownData) {
            if (!stepdownParams?.preSandISIP && !currentStage?.metrics?.preSandISIP) continue;
            const step = preSandStepdownData[i];
            const fracGradient = ((step?.pressure - (stepdownParams?.preSandISIP || currentStage?.metrics?.preSandISIP)) * 1000 / stepdownParams?.averageDepth);
            XCurrentPreSand.push(step.rate * (units === 'field' ? rtfoConversionFactors.rate : 1) || 1);
            YCurrentPreSand.push(fracGradient * (units === 'field' ? rtfoConversionFactors.fracGradient / 1000 : 1) || 1);
          };
          traces.push({
            x: XCurrentPreSand,
            y: YCurrentPreSand,
            mode: 'marker',
            type: 'scatter',
            xaxis: 'x1',
            yaxis: 'y1',
            name: `${currentRTFO?.currentStageName?.split('_')?.slice(1, )?.join(' ')} - Pre Sand`,
            marker: {
              size: 8,
              color: chartColors[7],
              opacity: 0.9
            },
            line: {
              width: 0
            },
            hoverinfo: 'text',
            hovertext: YCurrent.map(y => `Pre Sand FG: ${y?.toFixed(2)} ${units === 'field' ? 'psi/ft' : 'kPa/m'}`),
          });
        };

        // add Ppipe for all steps
        const XPipeFriction = [];
        const YPipeFriction = [];

        for (let i in PpipeArray) {
          const stepPpipe = PpipeArray[i];
          const step = stepdownData[i];
          const fracGradient = stepPpipe / stepdownParams?.averageDepth;
          XPipeFriction.push(step?.rate * (units === 'field' ? rtfoConversionFactors.rate : 1));
          YPipeFriction.push(fracGradient * (units === 'field' ? (rtfoConversionFactors.fracGradient / 1000) : 1));
        };

        traces.push({
          x: XPipeFriction,
          y: YPipeFriction,
          mode: 'marker',
          type: 'scatter',
          xaxis: 'x1',
          yaxis: 'y1',
          name: currentRTFO?.currentStageName?.split('_')?.slice(1, )?.join(' ') + ' - Ppipe',
          marker: {
            size: 8,
            color: 'red',
            symbol: 'square',
            // color: chartColors[0],
            opacity: 0.9
          },
          line: {
            width: 0
          },
          hoverinfo: 'text',
          hovertext: YPipeFriction.map(y => `Ppipe FG: ${y?.toFixed(2)} ${units === 'field' ? 'psi/ft' : 'kPa/m'}`),
        });

        // // add Power Law regression for Ppipe
        const pipeFrictionTrendlineX = 
          units === 'field' ?
          [
            powerLaw(0.01, PpipeCoefficients?.[0], PpipeCoefficients?.[1]), 
            powerLaw(100, PpipeCoefficients?.[0], PpipeCoefficients?.[1])
          ] :
          [
            powerLaw(0.1, PpipeCoefficients?.[0], PpipeCoefficients?.[1]), 
            powerLaw(100, PpipeCoefficients?.[0], PpipeCoefficients?.[1])
          ];
        const pipeFrictionTrendlineY = units === 'field' ? [0.01, 100] : [0.1, 100];
        traces.push({
          x: pipeFrictionTrendlineX,
          y: pipeFrictionTrendlineY,
          mode: 'lines',
          type: 'scatter',
          xaxis: 'x1',
          yaxis: 'y1',
          name: 'Ppipe Fit',
          line: {
            width: 3,
            color: 'red',
            dash: 'dash'
          },
          hoverinfo: 'text',
          hovertext: pipeFrictionTrendlineY.map(y => `Friction Gradient: ${y?.toFixed(2)} ${units === 'field' ? 'psi/ft' : 'kPa/m'}`),
        });

        
        // add autofit trendline
        const trendlineX = 
          units === 'field' ?
            [
              powerLaw(0.01, stepdownParams?.fcFrictionModel * rtfoConversionFactors.fc(stepdownParams?.plExpFrictionModel), stepdownParams?.plExpFrictionModel), 
              powerLaw(100, stepdownParams?.fcFrictionModel * rtfoConversionFactors.fc(stepdownParams?.plExpFrictionModel), stepdownParams?.plExpFrictionModel)
            ] :
            [
              powerLaw(0.1, stepdownParams?.fcFrictionModel, stepdownParams?.plExpFrictionModel), 
              powerLaw(100, stepdownParams?.fcFrictionModel, stepdownParams?.plExpFrictionModel)
            ];
        const trendlineY = units === 'field' ? [0.01, 100] : [0.1, 100];
        traces.push({
          x: trendlineX,
          y: trendlineY,
          mode: 'lines',
          type: 'scatter',
          xaxis: 'x1',
          yaxis: 'y1',
          name: 'Autofit',
          line: {
            width: 3,
            color: chartColors[0],
            dash: 'dash'
          },
          hoverinfo: 'text',
          hovertext: trendlineY.map(y => `Friction Gradient: ${y?.toFixed(2)} ${units === 'field' ? 'psi/ft' : 'kPa/m'}`),
        });

        // add SDT friction model trendline
        const wellName = currentRTFO?.currentStageName?.split('_')?.[1];
        const stageNumber = currentRTFO?.currentStageName?.split('_')?.[2]?.replace(/[^0-9]+/g, '');
        const cluster = currentRTFO?.perfSchedule?.wells?.[wellName?.toLowerCase()]?.stages?.[stageNumber]?.clusters;
        const depths = cluster && Object.keys(cluster)?.map(d => cluster[d]?.shotDepth);

        const mdAvg = depths?.reduce((a, b) => a + b, 0) / depths?.length;
        const lengthCasing1 = settings?.wells?.[wellName?.toLowerCase()]?.casing2?.md > 0 ? settings?.wells?.[wellName?.toLowerCase()]?.casing1?.md : mdAvg;
        const lengthCasing2 = mdAvg - lengthCasing1;

        let fcAvg = (stepdownParams?.fcCasing1 * lengthCasing1 + stepdownParams?.fcCasing2 * lengthCasing2) / (lengthCasing1 + lengthCasing2);
        const plExpAvg = (stepdownParams?.plExpCasing1 * lengthCasing1 + stepdownParams?.plExpCasing2 * lengthCasing2) / (lengthCasing1 + lengthCasing2);
        const trendlineSDTX = 
          units === 'field' ?
            [
              powerLaw(0.01, fcAvg * rtfoConversionFactors.fc(plExpAvg), plExpAvg), 
              powerLaw(100, fcAvg * rtfoConversionFactors.fc(plExpAvg), plExpAvg)
            ] :
            [
              powerLaw(0.1, fcAvg, plExpAvg), 
              powerLaw(100, fcAvg, plExpAvg)
            ];
        const trendlineSDTY = units === 'field' ? [0.01, 100] : [0.1, 100];
        traces.push({
          x: trendlineSDTX,
          y: trendlineSDTY,
          mode: 'lines',
          type: 'scatter',
          xaxis: 'x1',
          yaxis: 'y1',
          name: 'SDT Model Avg.',
          line: {
            width: 3,
            color: chartColors[5],
            dash: 'dot'
          },
          hoverinfo: 'text',
          hovertext: trendlineSDTY.map(y => `Friction Gradient: ${y?.toFixed(2)} ${units === 'field' ? 'psi/ft' : 'kPa/m'}`),
        });

        // add user trendline
        let fcAvgUser = (frictionModelParams?.fcCasing1 * lengthCasing1 + frictionModelParams?.fcCasing2 * lengthCasing2) / (lengthCasing1 + lengthCasing2);
        const plExpAvgUser = (frictionModelParams?.plExpCasing1 * lengthCasing1 + frictionModelParams?.plExpCasing2 * lengthCasing2) / (lengthCasing1 + lengthCasing2);
        const userTrendlineX = 
          units === 'field' ?
            [
              powerLaw(0.01, fcAvgUser, plExpAvgUser), 
              powerLaw(100, fcAvgUser, plExpAvgUser)
            ] :
            [
              powerLaw(0.1, fcAvgUser, plExpAvgUser), 
              powerLaw(100, fcAvgUser, plExpAvgUser)
            ];
        const userTrendlineY = [units === 'field' ? 0.01 : 0.1, 100];
        traces.push({
          x: userTrendlineX,
          y: userTrendlineY,
          mode: 'lines',
          type: 'scatter',
          xaxis: 'x1',
          yaxis: 'y1',
          name: 'Manual',
          line: {
            width: 3,
            color: '#333',
            dash: 'dot'
          },
          hoverinfo: 'text',
          hovertext: userTrendlineY.map(y => `Friction Gradient: ${y?.toFixed(2)} ${units === 'field' ? 'psi/ft' : 'kPa/m'}`),
        });

        return traces;
  
      } catch (error) {
        console.log(error);
        return [];
      };
    };
  }, [stepdownData, stepdownParams, currentRTFO?.currentStageName, currentRTFO?.perfSchedule?.wells, currentRTFO?.stageData, preSandStepdownData, units, PpipeCoefficients, settings?.wells, frictionModelParams?.fcCasing1, frictionModelParams?.fcCasing2, frictionModelParams?.plExpCasing1, frictionModelParams?.plExpCasing2, selectedStages, currentStage?.metrics?.preSandISIP, PpipeArray]);


  useEffect(() => {
    // add Power Law regression for Ppipe
    // add Ppipe for all steps
    const XPipeFriction = [];
    const YPipeFriction = [];

    for (let i in PpipeArray) {
      const stepPpipe = PpipeArray[i];
      const step = stepdownData[i];
      const fracGradient = stepPpipe / stepdownParams?.averageDepth;
      XPipeFriction.push(step?.rate * (units === 'field' ? rtfoConversionFactors.rate : 1));
      YPipeFriction.push(fracGradient * (units === 'field' ? (rtfoConversionFactors.fracGradient / 1000) : 1));
    };
    const powerRegression = new PowerRegression(XPipeFriction, YPipeFriction);

    handlePpipeFrictionFitUpdate(() => [powerRegression?.A, powerRegression?.B]);
  }, [PpipeArray, handlePpipeFrictionFitUpdate, stepdownData, stepdownParams?.averageDepth, units]);

  const layout = {
    // grid: {rows: 3, columns: 2, pattern: 'independent'},
    yaxis: { // Treating pressure y axis
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      title: {
        text: `Friction Gradient (${units === 'field' ? 'psi/ft' : 'kPa/m'})`,
        font: {
          size: 12
        },
      },
      anchor: 'x',
      // autorange: true,
      range: units === 'field' ? [-2, 1] : [-1, 2],
      side: 'left',
      type: 'log'
      // uirevision: 'true'
    },
    xaxis: { // Date x axis
      title: {
        text: `Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`,
        font: {
          size: 12
        }
      },
      range: units === 'field' ? [1, 3] : [0, 2],
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      anchor: 'y',
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across",
      type: 'log'
    },
    hovermode: "x",
    hoverdistance: 10, // Distance to show hover label of data point
    spikedistance: 1000,
    autosize: true,
    showlegend: true,
    legend: {
      orientation: "h",
      yanchor: "top",
      font: {
        color: darkMode ? 'lightgrey' : 'grey',
        size: 10
      },
      // x: 1,
      // xanchor: 'right',
      // xref: 'paper',
      y: -0.15
    },
    annotations: [
      {
        text: "Stepdown Friction Model",
          font: {
          size: 14,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: 1.15, //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      {
        text: `Pad: ${currentRTFO?.name} | Well: ${currentStage?.wellName} | Stage: ${currentStage?.stageNumber || "No stage selected"}`,
          font: {
          size: 10,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: 1.075, //position in y domain
        xref: 'paper',
        yref: 'paper',
      }
    ],
    shapes: [
      
    ],
    uirevision: 'true',
    margin: {"t": 60, "l": 60, "r": 60, "b": 60},
    images: [
      {
        x: (0.05),
        y: (1.05),
        sizex: (0.1),
        sizey: 0.1,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xref: "page",
        yref: "y1 domain",
        xanchor: "right",
        yanchor: "bottom",
        opacity: 0.75
      }
    ],
    plot_bgcolor: "transparent",
    paper_bgcolor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]
  };


  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: "RTFO-" + (new Date().toISOString().split('T')[0]),
      // filename: currentFBA?.name + "-" + (new Date().toISOString().split('T')[0]),
      height: 550,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };

  return (
    <Plot
      data={data}
      layout={layout}
      config={config}
      useResizeHandler={true}
      style={{width: '100%', height: '100%'}}
    />
  );

  // add fit trendline
  function powerLaw(y, coeff, exp) {
    try {
      return (y / coeff) ** (1 / exp);
    } catch (error) {
      console.log(error);
      return 1;
    };
  };
};
